/**
 * Domain types for earn position + acknowledgment routes:
 *   - POST `/earn/vaults/positions`
 *   - POST `/earn/portfolio/positions`
 *   - POST `/earn/vaults/acknowledge`
 *
 * Owns `EarnWithdrawalRequest(Status)` — also consumed by {@link ./earn-queue}.
 */

import { Replace } from "../../reducers/type-util/primitive";
import { EarnUserPositionApi, EarnWithdrawalRequestApi } from "../api/earn-positions";
import { EarnVaultRecord } from "./earn";

/** Mirrors BE `EarnWithdrawalRequestStatus` u8 discriminant. */
export enum EarnWithdrawalRequestStatus {
    Pending = 0,
    Partial = 1,
    Fulfilled = 2,
    Cancelled = 3
}

/** Per-user, per-vault position. `acknowledgedAt` non-null ⇒ user has accepted risk modal. */
export type EarnUserPosition = Replace<
    EarnUserPositionApi,
    {
        shareBalance: bigint;
        lpBalance?: bigint;
        costBasisAssets: bigint;
        realizedPnlAssets: bigint;
        feesPaidAssets: bigint;
        lastSyncedSlot: bigint;
        acknowledgedAt?: string;
    }
>;

export interface EarnWithdrawalFill {
    mint: string;
    amount: bigint;
}

/** Single row from `earn_withdrawal_request`. Shared with the queue endpoint. */
export type EarnWithdrawalRequest = Replace<
    EarnWithdrawalRequestApi,
    {
        sharesRequested: bigint;
        sharesFilled: bigint;
        sharesClaimed: bigint;
        status: EarnWithdrawalRequestStatus;
        fulfilledAt?: string;
        cancelledAt?: string;
        payoutMint?: string;
        fills: EarnWithdrawalFill[];
    }
>;

/**
 * POST `/earn/vaults/positions` (and each element of `/earn/portfolio/positions`).
 * Restructured into `vault + position + openWithdrawalRequests` for ergonomic consumption.
 * The wire shape keeps `vault` as a nested field (only `EarnVault` columns are flattened
 * one level deeper inside `EarnVaultWithLatestSnapshot`).
 */
export type EarnVaultPositionResponse = {
    vault: EarnVaultRecord;
    position?: EarnUserPosition;
    openWithdrawalRequests: EarnWithdrawalRequest[];
};
